import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Settings as SettingsIcon,
  Building,
  Users,
  Shield,
  MapPin,
  Phone,
  Mail,
  Clock,
  AlertTriangle,
  Loader2,
  Edit,
  Save,
  X,
  FileText,
  ExternalLink,
  Plus,
  Trash2,
  UserX
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useCompany } from '@/hooks/use-company';
import { useUsers, USER_ROLES } from '@/hooks/use-users';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Calculator } from 'lucide-react';
import { useCompanyTier } from '@/hooks/use-company-tier';
import { SubscriptionRequiredModal } from '@/components/settings/SubscriptionRequiredModal';
import { LocationManagement } from '@/components/settings/LocationManagement';

// Color tag interfaces (these could be moved to a database table later)
interface ColorTag {
  id: string;
  name: string;
  color: string;
  description: string;
  assignmentRule: string;
}

// Color tags data (this could be moved to a database table in the future)
const colorTags: ColorTag[] = [
  { id: '1', name: 'VIP', color: 'Gold', description: 'Yüksek satın alma geçmişi olan premium müşteriler', assignmentRule: 'Satın Alma > 10.000₺' },
  { id: '2', name: 'Normal', color: 'Blue', description: 'Standart müşteriler', assignmentRule: 'Varsayılan atama' },
  { id: '3', name: 'Problem', color: 'Red', description: 'Özel ilgi gerektiren müşteriler', assignmentRule: 'Sadece manuel atama' },
  { id: '4', name: 'Potansiyel', color: 'Black', description: 'Potansiyel yüksek değerli müşteriler', assignmentRule: 'Ziyaret sıklığı > 5' }
];

export default function Settings() {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Database hooks
  const { companyProfile, loading: companyLoading, error: companyError, updateCompany } = useCompany();
  const { users, loading: usersLoading, error: usersError, updateUserRole, createUser, deleteUser, deleteAccount } = useUsers();
  const { settings: profitSettings, updateSettings: updateProfitSettings, loading: profitSettingsLoading } = useProfitSettings();
  const { tier } = useCompanyTier();
  
  // Edit states
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [editedCompany, setEditedCompany] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: ''
  });
  
  // Privacy & Safety settings
  const [retentionHours] = useState(72);
  const [phoneMasking, setPhoneMasking] = useState(true);
  const [emailMasking, setEmailMasking] = useState(true);
  
  // Password change state
  const { user, signOut, userRole } = useAuth();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // User management state
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ email: '', full_name: '', password: '', role: 'sales' });
  const [deleteUserTarget, setDeleteUserTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  // Account deletion state
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteAccountEmail, setDeleteAccountEmail] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Update edited company when companyProfile changes
  useEffect(() => {
    if (companyProfile) {
      setEditedCompany({
        name: companyProfile.name || '',
        address: companyProfile.address || '',
        phone: companyProfile.phone || '',
        email: companyProfile.email || '',
        website: companyProfile.website || ''
      });
    }
  }, [companyProfile]);

  const handleSaveCompany = async () => {
    if (!companyProfile) return;
    
    const success = await updateCompany(editedCompany);
    if (success) {
      setIsEditingCompany(false);
    }
  };

  const handleCancelEdit = () => {
    if (companyProfile) {
      setEditedCompany({
        name: companyProfile.name || '',
        address: companyProfile.address || '',
        phone: companyProfile.phone || '',
        email: companyProfile.email || '',
        website: companyProfile.website || ''
      });
    }
    setIsEditingCompany(false);
  };

  const getRoleDisplayName = (role: string) => {
    const roleObj = USER_ROLES.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const handleChangePassword = async () => {
    // Validation
    if (!user?.email) {
      toast({
        title: "Hata",
        description: "Kullanıcı bilgileri bulunamadı. Lütfen tekrar giriş yapın.",
        variant: "destructive"
      });
      return;
    }

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        title: "Hata",
        description: "Lütfen tüm alanları doldurun.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Hata",
        description: "Yeni şifre en az 6 karakter olmalıdır.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Hata",
        description: "Yeni şifreler eşleşmiyor.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      toast({
        title: "Hata",
        description: "Yeni şifre mevcut şifre ile aynı olamaz.",
        variant: "destructive"
      });
      return;
    }

    setIsChangingPassword(true);

    try {
      // First, verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword
      });

      if (signInError) {
        toast({
          title: "Hata",
          description: "Mevcut şifre yanlış.",
          variant: "destructive"
        });
        setIsChangingPassword(false);
        return;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Clear the form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      toast({
        title: "Başarılı",
        description: "Şifreniz başarıyla değiştirildi. Güvenlik nedeniyle çıkış yapılıyor...",
      });

      // Logout user after successful password change
      await signOut();
      navigate('/login');
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast({
        title: "Hata",
        description: error.message || "Şifre değiştirilirken bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAddUserClick = () => {
    if (tier === 'free') {
      setShowSubscriptionModal(true);
    } else {
      setNewUserData({ email: '', full_name: '', password: '', role: 'sales' });
      setShowAddUserDialog(true);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.full_name || !newUserData.password) {
      toast({ title: 'Hata', description: 'Lütfen tüm zorunlu alanları doldurun', variant: 'destructive' });
      return;
    }
    if (newUserData.password.length < 6) {
      toast({ title: 'Hata', description: 'Şifre en az 6 karakter olmalıdır', variant: 'destructive' });
      return;
    }
    setIsCreatingUser(true);
    const success = await createUser(newUserData);
    setIsCreatingUser(false);
    if (success) {
      setShowAddUserDialog(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setIsDeletingUser(true);
    await deleteUser(deleteUserTarget.id);
    setIsDeletingUser(false);
    setDeleteUserTarget(null);
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountEmail) {
      toast({ title: 'Hata', description: 'Lütfen e-posta adresinizi girin', variant: 'destructive' });
      return;
    }
    setIsDeletingAccount(true);
    const success = await deleteAccount(deleteAccountEmail);
    setIsDeletingAccount(false);
    if (success) {
      setShowDeleteAccountDialog(false);
      await signOut();
      navigate('/login');
    }
  };

  const isAdmin = userRole === 'admin';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Ayarlar</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Sistem yapılandırması ve tercihlerini yönetin</p>
          </div>
        </div>

        <Tabs defaultValue="company" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              <span className="hidden sm:inline">Şirket</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Kullanıcılar</span>
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Gizlilik</span>
            </TabsTrigger>
          </TabsList>

          {/* Company & Locations Tab */}
          <TabsContent value="company" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Şirket Bilgileri</CardTitle>
                  {!isEditingCompany ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingCompany(true)}
                      disabled={companyLoading}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Düzenle
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                      >
                        <X className="w-4 h-4 mr-2" />
                        İptal
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveCompany}
                        disabled={companyLoading}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Kaydet
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {companyError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{companyError}</AlertDescription>
                  </Alert>
                )}
                
                {companyLoading ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </div>
                  </div>
                ) : companyProfile ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="companyName">Şirket Adı</Label>
                        <Input
                          id="companyName"
                          value={isEditingCompany ? editedCompany.name : companyProfile.name}
                          onChange={(e) => setEditedCompany(prev => ({ ...prev, name: e.target.value }))}
                          disabled={!isEditingCompany}
                          className={!isEditingCompany ? "bg-muted" : ""}
                        />
                      </div>
                      <div>
                        <Label htmlFor="companyWebsite">Web Sitesi</Label>
                        <Input
                          id="companyWebsite"
                          value={isEditingCompany ? editedCompany.website : companyProfile.website}
                          onChange={(e) => setEditedCompany(prev => ({ ...prev, website: e.target.value }))}
                          disabled={!isEditingCompany}
                          className={!isEditingCompany ? "bg-muted" : ""}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="companyAddress">Adres</Label>
                      <Textarea
                        id="companyAddress"
                        value={isEditingCompany ? editedCompany.address : companyProfile.address}
                        onChange={(e) => setEditedCompany(prev => ({ ...prev, address: e.target.value }))}
                        disabled={!isEditingCompany}
                        className={!isEditingCompany ? "bg-muted" : ""}
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="companyPhone">Telefon</Label>
                        <Input
                          id="companyPhone"
                          value={isEditingCompany ? editedCompany.phone : companyProfile.phone}
                          onChange={(e) => setEditedCompany(prev => ({ ...prev, phone: e.target.value }))}
                          disabled={!isEditingCompany}
                          className={!isEditingCompany ? "bg-muted" : ""}
                        />
                      </div>
                      <div>
                        <Label htmlFor="companyEmail">E-posta</Label>
                        <Input
                          id="companyEmail"
                          type="email"
                          value={isEditingCompany ? editedCompany.email : companyProfile.email}
                          onChange={(e) => setEditedCompany(prev => ({ ...prev, email: e.target.value }))}
                          disabled={!isEditingCompany}
                          className={!isEditingCompany ? "bg-muted" : ""}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>Şirket bilgileri yüklenemedi</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <LocationManagement />

            {/* Profit Calculation Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Kar Hesaplama Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertDescription>
                    Kar hesaplamalarında Mars fiyatlarını kullanmak için bu ayarı etkinleştirin.
                    Satış tarihine göre o tarihteki Mars fiyatı kullanılacaktır.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Mars Fiyatlarını Kullan</div>
                      <div className="text-sm text-muted-foreground">
                        Kar hesaplamalarında Mars fiyatlarını kullan (ürünün satış tarihindeki fiyatı)
                      </div>
                    </div>
                    <Switch
                      checked={profitSettings.enabled}
                      onCheckedChange={(checked) => updateProfitSettings({ enabled: checked })}
                      disabled={profitSettingsLoading}
                    />
                  </div>

                  {profitSettings.enabled && (
                    <>
                      <Separator />
                      <div>
                        <Label htmlFor="mars-price-tier">Mars Fiyat Tipi</Label>
                        <Select
                          value={profitSettings.marsPriceTier}
                          onValueChange={(value) => updateProfitSettings({ marsPriceTier: value as any })}
                          disabled={profitSettingsLoading}
                        >
                          <SelectTrigger id="mars-price-tier" className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pesin">Peşin Fiyat</SelectItem>
                            <SelectItem value="30">30 Gün Vadeli</SelectItem>
                            <SelectItem value="60">60 Gün Vadeli</SelectItem>
                            <SelectItem value="90">90 Gün Vadeli</SelectItem>
                            <SelectItem value="120">120 Gün Vadeli</SelectItem>
                            <SelectItem value="150">150 Gün Vadeli</SelectItem>
                            <SelectItem value="180">180 Gün Vadeli</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Kar hesaplamalarında kullanılacak Mars fiyat tipini seçin. 
                          Satış tarihine göre o tarihteki fiyat kullanılacaktır.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users & Roles Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Kullanıcılar ve Roller</CardTitle>
                  {isAdmin && (
                    <Button size="sm" onClick={handleAddUserClick}>
                      <Plus className="w-4 h-4 mr-2" />
                      Kullanıcı Ekle
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {usersError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{usersError}</AlertDescription>
                  </Alert>
                )}

                {usersLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-3 border rounded-lg bg-muted/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Skeleton className="h-5 w-32" />
                          <Skeleton className="h-5 w-16" />
                          <Skeleton className="h-5 w-20" />
                        </div>
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-48" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : users.length > 0 ? (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{u.full_name || 'İsimsiz Kullanıcı'}</h4>
                            <Badge variant={u.status === 'active' ? 'default' : 'secondary'}>
                              {u.status === 'active' ? 'aktif' : u.status === 'pending' ? 'beklemede' : 'pasif'}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {getRoleDisplayName(u.role)}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <div className="flex items-center gap-1 mb-1">
                              <Mail className="w-3 h-3" />
                              {u.email || 'E-posta belirtilmemiş'}
                            </div>
                            <div className="text-xs">
                              Oluşturulma: {new Date(u.created_at).toLocaleDateString('tr-TR')}
                            </div>
                          </div>
                        </div>
                        {isAdmin && u.id !== user?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteUserTarget({ id: u.id, name: u.full_name || u.email || 'Kullanıcı' })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert>
                    <Users className="h-4 w-4" />
                    <AlertDescription>Henüz kullanıcı bulunmamaktadır.</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Privacy Tab */}
          <TabsContent value="privacy" className="space-y-6">
            {/* Change Password Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Şifre Değiştir
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">Mevcut Şifre</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        placeholder="Mevcut şifrenizi girin"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.current ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="newPassword">Yeni Şifre</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="Yeni şifrenizi girin (en az 6 karakter)"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.new ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Şifre en az 6 karakter olmalıdır
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">Yeni Şifre Tekrar</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Yeni şifrenizi tekrar girin"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.confirm ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword}
                    className="w-full"
                  >
                    {isChangingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Değiştiriliyor...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Şifreyi Değiştir
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Gizlilik ve Güvenlik Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 border rounded-lg bg-destructive/5 border-destructive/20">
                    <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-destructive">Güvenlik Anahtarı</h4>
                          <p className="text-sm text-muted-foreground">
                            Acil durum güvenlik protokollerini başlat
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            toast({
                              title: "Güvenlik Anahtarı Başlatıldı",
                              description: "Acil durum güvenlik protokolleri etkinleştirildi",
                              variant: "destructive",
                            });
                          }}
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Güvenlik Anahtarını Başlat
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <Label htmlFor="retention">Veri Saklama (saat)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <Input
                        id="retention"
                        type="number"
                        value={retentionHours}
                        disabled
                        className="w-32 bg-muted"
                        min="1"
                        max="168"
                      />
                      <span className="text-sm text-muted-foreground">saat (maks 7 gün)</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Veri saklama politikası sistem yöneticisi tarafından yönetilir
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium">Veri Maskeleme</h4>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Telefon Numarası Maskeleme</div>
                        <div className="text-sm text-muted-foreground">
                          Telefon numaralarını +90 (212) ***-*567 şeklinde göster
                        </div>
                      </div>
                      <Switch
                        checked={phoneMasking}
                        onCheckedChange={setPhoneMasking}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">E-posta Adresi Maskeleme</div>
                        <div className="text-sm text-muted-foreground">
                          E-postaları m***@ornek.com şeklinde göster
                        </div>
                      </div>
                      <Switch
                        checked={emailMasking}
                        onCheckedChange={setEmailMasking}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">Aktivite Logları</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/activity-log')}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Logları Görüntüle
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </div>
                    <Alert>
                      <FileText className="h-4 w-4" />
                      <AlertDescription>
                        Tüm sistem aktivitelerini görüntüleyin ve filtreleyin. Kim ne zaman hangi işlemi yaptığını takip edin.
                      </AlertDescription>
                    </Alert>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-medium mb-4">Renk Etiketleri (Sadece Görüntüleme)</h4>
                    <div className="space-y-3">
                      {colorTags.map((tag) => (
                        <div key={tag.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              tag.color === 'Gold' ? 'bg-yellow-400 border-yellow-500' :
                              tag.color === 'Blue' ? 'bg-blue-400 border-blue-500' :
                              tag.color === 'Red' ? 'bg-red-400 border-red-500' :
                              'bg-gray-800 border-gray-900'
                            }`} />
                            <div>
                              <h4 className="font-medium">{tag.name}</h4>
                              <p className="text-sm text-muted-foreground">{tag.description}</p>
                              <p className="text-xs text-muted-foreground">Kural: {tag.assignmentRule}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="capitalize">
                            {tag.color}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delete Account Card */}
            <Card className="border-destructive/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <UserX className="w-5 h-5" />
                  Hesabı Sil
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Hesabınızı sildiğinizde kullanıcı profiliniz kalıcı olarak kaldırılacaktır.
                  Son kullanıcıysanız firma erişimi devre dışı bırakılacaktır.
                </p>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setDeleteAccountEmail('');
                    setShowDeleteAccountDialog(true);
                  }}
                >
                  <UserX className="w-4 h-4 mr-2" />
                  Hesabımı Sil
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
            <DialogDescription>Firmanıza yeni bir kullanıcı ekleyin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="newUserName">Ad Soyad <span className="text-destructive">*</span></Label>
              <Input
                id="newUserName"
                value={newUserData.full_name}
                onChange={(e) => setNewUserData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Kullanıcının adı soyadı"
                disabled={isCreatingUser}
              />
            </div>
            <div>
              <Label htmlFor="newUserEmail">E-posta <span className="text-destructive">*</span></Label>
              <Input
                id="newUserEmail"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="kullanici@ornek.com"
                disabled={isCreatingUser}
              />
            </div>
            <div>
              <Label htmlFor="newUserPassword">Şifre <span className="text-destructive">*</span></Label>
              <Input
                id="newUserPassword"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="En az 6 karakter"
                disabled={isCreatingUser}
              />
            </div>
            <div>
              <Label htmlFor="newUserRole">Rol</Label>
              <Select
                value={newUserData.role}
                onValueChange={(value) => setNewUserData(prev => ({ ...prev, role: value }))}
                disabled={isCreatingUser}
              >
                <SelectTrigger id="newUserRole">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddUserDialog(false)} disabled={isCreatingUser}>
                İptal
              </Button>
              <Button className="flex-1" onClick={handleCreateUser} disabled={isCreatingUser}>
                {isCreatingUser ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  'Kullanıcı Oluştur'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUserTarget} onOpenChange={(open) => !open && setDeleteUserTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteUserTarget?.name}</strong> kullanıcısını silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingUser}>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={isDeletingUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                'Sil'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation */}
      <Dialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Hesabı Sil</DialogTitle>
            <DialogDescription>
              Bu işlem geri alınamaz. Hesabınız kalıcı olarak silinecektir.
              Onaylamak için e-posta adresinizi girin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="deleteConfirmEmail">E-posta Adresiniz</Label>
              <Input
                id="deleteConfirmEmail"
                type="email"
                value={deleteAccountEmail}
                onChange={(e) => setDeleteAccountEmail(e.target.value)}
                placeholder={user?.email || 'E-posta adresiniz'}
                disabled={isDeletingAccount}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteAccountDialog(false)} disabled={isDeletingAccount}>
                İptal
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount || deleteAccountEmail !== user?.email}
              >
                {isDeletingAccount ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Siliniyor...
                  </>
                ) : (
                  'Hesabımı Sil'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Required Modal */}
      <SubscriptionRequiredModal
        open={showSubscriptionModal}
        onOpenChange={setShowSubscriptionModal}
      />
    </Layout>
  );
}